/**
 * Every app-shell string the console owns, in one place, matching the label
 * layer ("mapped here and nowhere else... every string in the label layer" is the acceptance
 * bar). The `#processing` work-list copy itself is server-generated (backend `condition-copy.ts`
 * — one label layer, not two, since the backend already owns the domain mapping); this file is
 * only the chrome and empty/loading states that never reach the backend.
 */
import { formatDuration } from './format-duration';
import { formatDay, formatStatusDate } from './format-date';

export const labels = {
  appName: 'Ops Console',
  // The backend's own reason follows, so the operator reads why a guarded write was refused.
  actionFailed: (reason: string) => `That did not work: ${reason}`,
  // A control the signed-in role cannot use, and the text of a `403` from the backend (ADR 0040).
  roleDenied: 'Your role does not allow this.',
  // The button in a load-error message, on every screen that has one.
  retry: 'Try again',
  searchPlaceholder: 'Find a document…',
  // The name of the last table column, whose head cell is empty on screen (it holds the row's button).
  tableActions: 'Actions',
  // QA-163: the stored PDF or image of a document, on Not processed and on its document page.
  sourceFile: {
    show: 'Show the file',
    hide: 'Hide the file',
    loading: 'Loading the file…',
    missing: 'The file is no longer stored.',
    unsupported: 'This file is not a PDF or an image, so the console cannot show it.',
    // The cap is `MAX_SHOWN_FILE_BYTES` in `backend/src/doc-record/source-file.ts`.
    tooLarge: 'This file is larger than 200 MB, so the console does not show it.',
    cannotShow: 'This browser cannot show this file here. Open it in a new tab.',
    loadFailed: "Couldn't load the file. Try again.",
    openInNewTab: 'Open in a new tab',
    frameTitle: (docId: string) => `The file of ${docId}`,
  },
  // Ft4 dense colophon (`design.md`): not a sitemap — the facts an operator needs when they
  // screenshot this page into a thread.
  colophon: {
    api: 'reads /api on',
  },
  // QA-126: the small info button next to a label. `text` is the caller's own label.
  infoTip: {
    about: (label: string) => `About ${label}`,
  },
  nav: {
    // QA-92: the rail's toggle, and the avatar that opens the account menu.
    toggle: 'Menu',
    account: 'Account menu',
    // The account menu shows who is signed in: the name, then the role.
    roleName: { viewer: 'Viewer', admin: 'Admin' } as const,
    // SSO-22: a member of the group Super admins has the console role admin; the menu names the level.
    superAdminName: 'Super admin',
    // QA-106: the header's theme button. The label names the action, not the state.
    themeToDark: 'Switch to dark theme',
    themeToLight: 'Switch to light theme',
    processing: 'Processing',
    documents: 'Documents',
    notProcessed: 'Not processed',
    extractionRules: 'Extraction rules',
    testExtraction: 'Test extraction',
    settings: 'Settings',
    // SSO-22: the link to the Keycloak user admin console, for a super admin only. It opens in a new tab.
    userAdmin: 'User admin',
    userAdminNewTab: 'User admin, opens in a new tab',
  },
  // The console's one dialog (`lib/ask.tsx`). Every guarded write reads its buttons and its
  // field-level messages from here, so "Cancel" is the same word on every screen.
  dialog: {
    cancel: 'Cancel',
    optional: 'optional',
    required: 'Fill this in to continue.',
    wholeNumber: 'Enter a whole number, 0 or more.',
  },
  login: {
    title: 'Sign in',
    lede: 'The operator console for the MBGE document pipeline. You sign in with your MBGE account.',
    submit: 'Sign in',
    signOut: 'Sign out',
    noRole: 'Your account has no access to the Ops Console. Ask an admin to give you a role.',
    expired: 'The sign-in took too long. Sign in again.',
    failed: 'Sign-in failed. Try again, or ask an admin.',
  },
  pageTitles: {
    documents: 'Documents',
    docRecord: 'Document record',
    notProcessed: 'Not processed',
    extractionRules: 'Extraction rules',
    settings: 'Settings',
    notFound: "This page doesn't exist",
  },
  processing: {
    cleanDayHeadline: 'Nothing is over its limit.',
    cleanDaySubline: (count: string) => `Checked moments ago, ${count} documents today.`,
    everySourceAnswered: 'every source answered',
    needsYouHeading: 'Needs you',
    drainHeading: 'Waiting for human review',
    drainEmpty: 'Nothing is waiting for human review.',
    spotCheckHeading: 'Waiting for a random check',
    spotCheckEmpty: 'Nothing is waiting for a random check.',
    importHeading: 'Historical import',
    importReadOnly: 'read-only',
    importEta: (eta: string) => `ETA ${eta}`,
    importNoEtaYet: 'ETA not yet known',
    loading: 'Loading pipeline status…',
    loadFailed: "Couldn't reach the console's own backend. Try reloading.",
    firstPassHeading: 'First pass movement',
    firstPassWindow: (start: string, end: string) => `Counts from ${start} to ${end}.`,
    firstPassColumns: { service: 'Stage', type: 'Document type', waiting: 'Waiting', inProgress: 'In progress', done: 'Done last 7 days', ofWhich: 'Of which', sentOn: 'Sent on', notProcessed: 'Not processed', split: 'Outcome counts' },
    firstPassServices: { orchestration: 'Intake', extraction: 'Extraction', validation: 'Validation' },
    firstPassNotes: {
      orchestration: 'Documents in the inbox that are not picked up yet.',
      extraction: 'Reads the fields. A failure goes to Not processed.',
      validation: 'Checks the fields. Runs inside the extraction call.',
    },
    firstPassGate: (counts: string) => `Gate: ${counts}`,
    firstPassFailure: (counts: string) => `Failure: ${counts}`,
    firstPassNone: 'none',
    firstPassOldest: (age: string) => `oldest ${age}`,
  },
  // QA-69, QA-85, QA-99: the paging control that **Test extraction**, **Documents**, and the **Not
  // processed** groups and **Ignored** share. `noOlder` is the text of an empty later page.
  paging: { older: 'Older', newer: 'Newer', noOlder: 'No older items.' },
  documents: {
    browse: {
      sub: 'Documents with a result, newest activity first. A document that did not process is on Not processed. Type in the search box above to look for one.',
      // The list region's name says what it holds, not its order: the operator can re-sort it.
      listLabel: 'Document list',
      loading: 'Loading documents…',
      empty: 'No documents on this page.',
      joinKey: (name: string, value: string) => `${name} ${value}`,
      outcomes: {
        accepted: 'Accepted',
        review: 'Needs review',
        reviewed: 'Reviewed',
      },
      // QA-130: the ⓘ text of the Needs review status, on the Status head and the document page.
      needsReviewTip: "MBGE could not confirm some fields. The first result is available, and a reviewer hasn't checked it yet.",
      // QA-115: the filter bar and the sortable table. The backend has no filter, so the filters
      // and the sort work on the page on the screen, and the scope line says so.
      filters: {
        label: 'Filter documents',
        status: 'Status',
        type: 'Type',
        from: 'From',
        to: 'To',
        any: 'All',
        clear: 'Clear filters',
        scope: (shown: number, loaded: number) =>
          `Showing ${shown} of the ${loaded} documents on this page. Filters and sorting apply to this page only.`,
        noMatch: 'No document on this page matches the filters. Clear them, or use Older for more.',
      },
      columns: { document: 'Document', type: 'Type', status: 'Status', at: 'Last activity' },
      sortMark: { ascending: '↑', descending: '↓' },
    },
    resultsHeading: (query: string) => `Search results for “${query}”`,
    searching: (days: number) => `Searching the last ${days} days…`,
    searchingAll: 'Searching all history…',
    stillLooking: (days: number) => `Searched the last ${days} days, still looking…`,
    noResultsYet: (days: number) => `No documents found in the last ${days} days.`,
    noResultsAll: 'No documents found, even after searching all history.',
    stopped: (days: number) => `Stopped after searching the last ${days} days.`,
    stop: 'Stop',
    resultCount: (count: number) => (count === 1 ? '1 document found' : `${count} documents found`),
    foundHeadline: 'Found.',
    notFoundHeadline: "Couldn't find that document.",
    notFoundFallback: 'No record of it yet. Check the id, or ask whoever filed it.',
    onNotProcessed: (group: string) => `This document is on Not processed: ${group}.`,
    onNotProcessedIgnored: (group: string) => `This document is on Not processed, ignored: ${group}.`,
    viewDocument: 'View →',
    loadFailed: "Couldn't reach the console's own backend. Try reloading.",
    columns: {
      document: 'Document',
      pedimento: 'Pedimento',
      cove: 'COVE',
      invoice: 'Invoice',
      caseKey: 'Case key',
    },
  },
  docRecord: {
    loading: 'Loading…',
    loadFailed: "Couldn't reach the console's own backend. Try reloading.",
    notFound: 'No record of this document. It has no result and is not on Not processed. Check the id.',
    deadLetter: {
      heading: (group: string) => `Not processed: ${group}`,
      headingIgnored: (group: string) => `Not processed (ignored): ${group}`,
      ignoredBy: (by: string) => (by ? `Ignored by ${by}.` : 'Ignored.'),
      reason: 'Reason',
      filedAs: (filed: string, looksLike: string) => `Filed as ${filed}, looks like ${looksLike}.`,
      since: 'Since',
      attempt: 'Attempt',
      seeIt: 'See it on Not processed →',
    },
    auditTrailHeading: 'Audit trail',
    gateReportHeading: 'Gate report',
    gateReportUnavailable: 'The gate report is not recorded for this document.',
    showRaw: 'Show the raw file',
    hideRaw: 'Hide the raw file',
    runAgain: 'Run again',
    // QA-150: Run again is not shown while review, a random check, or a parked handoff holds the document.
    held: {
      'human-review': { text: 'In human review.', tip: 'Run again works after the review.' },
      'random-check': { text: 'In a random check.', tip: 'Run again works after the check.' },
      handoff: { text: 'Waiting to go to review.', tip: 'Run again works after MBGE hands it to review.' },
    },
    runAgainConfirm: (docId: string) => ({
      title: `Run ${docId} again?`,
      body: 'MBGE processes the same document again with the active extraction rules. When the new run completes, its result replaces the current one, and the old result stays in history. A scanned document starts a paid job.',
    }),
    runAgainStarted: (docId: string, attempt: number) =>
      `${docId} runs again as attempt ${attempt}. The result replaces the current one when it completes.`,
    langfuseTrace: 'View the Langfuse trace →',
    viewExtractionRules: 'The version that produced this →',
    counterpart: 'View the other document →',
    columns: { field: 'Field', value: 'Value', source: 'Source', confidence: 'Confidence' },
    noConfidenceSignal: 'No confidence signal is recorded for these fields.',
    sourceOnce: (source: string) => `Every value comes from ${source}.`,
    detectedType: 'Detected type',
    layout: 'Layout',
    xmlCoverage: 'XML coverage',
    gateColumns: { rule: 'Rule', status: 'Status', message: 'Message' },
    escalatedBecause: 'Escalated because',
    // QA-113: the escalation names the values it is about; each one scrolls to its cell.
    valuesToCheck: 'Values to check',
    cellInGroup: (group: string, row: number, column: string) => `${group}, row ${row}, ${column}`,
    flaggedCellsHeading: 'Cells the row check could not confirm',
    cellNotConfirmed: 'check this cell',
    audit: {
      skillVersion: 'Skill version',
      modelId: 'Model id',
      gateVerdict: 'Gate verdict',
      tier: 'Tier',
      attempt: 'Attempt',
      reviewedBy: 'Reviewed by',
      readerNotes: 'Reader notes',
    },
    rawResultFile: 'result.json',
    rawProvenanceFile: 'provenance.json',
  },
  notProcessed: {
    loading: 'Loading…',
    loadFailed: "Couldn't reach the console's own backend. Try reloading.",
    classHeadings: {
      poison: 'Unreadable file',
      misfiled: 'Wrong folder',
      unroutable: 'Unknown folder',
      collision: 'Duplicate filename',
      internal: 'Pipeline failure',
    } as Record<string, string>,
    classWhatToDo: {
      poison: 'Ask whoever supplied it for a copy that opens. If the reason says it is too large, ask for it in smaller files.',
      misfiled: 'Re-file it under the type the page suggests, then ignore this entry.',
      unroutable: 'Give it one of the five types, or rule it out of scope.',
      collision: 'Rename one of the two, or ignore it if it really is the same document.',
      internal: 'An engineering problem, not a filing problem. Try again once it is fixed.',
    } as Record<string, string>,
    ignoredHeading: 'Ignored',
    routeContext: (heading: string) => `Opened from Processing: showing ${heading.toLowerCase()} documents.`,
    emptyGroup: 'Nothing in this group.',
    copyList: {
      poison: 'Copy list for the sender',
      misfiled: 'Copy list for filing',
      unroutable: 'Copy list for filing',
      collision: 'Copy list for filing',
      internal: 'Copy list for engineering',
    } as Record<string, string>,
    copyListFallback: 'Copy list',
    copyTip: (count: number, isFloor: boolean) =>
      count === 1 && !isFloor
        ? 'Copies this document with its reason, ready to paste into an email or Slack. Nothing changes here.'
        : `Copies ${isFloor ? 'at least' : 'all'} ${count} document${count === 1 ? '' : 's'} with the reason for each, ready to paste into an email or Slack. Nothing changes here.`,
    ignoredTip: "Documents someone set aside with 'This is fine, ignore it'. Un-ignore brings one back.",
    copied: 'Copied.',
    copyFailed: "Couldn't copy the list. Select the copied-list text and copy it manually.",
    showCopiedList: 'Show copied list',
    hideCopiedList: 'Hide copied list',
    tryAgain: 'Try again',
    sentAgain: 'Sent again. The row updates when the new run ends.',
    tryingAgain: 'Trying again',
    // QA-158: a document that review holds. Try again waits until the hold ends.
    held: {
      'human-review': { line: 'In human review.', tip: 'Try again works after the review.' },
      'random-check': { line: 'In a random check.', tip: 'Try again works after the check.' },
      handoff: { line: 'Waiting to go to review.', tip: 'Try again works after MBGE hands it to review.' },
    } as Record<'human-review' | 'random-check' | 'handoff', { line: string; tip: string }>,
    acknowledge: 'This is fine, ignore it',
    unignore: 'Un-ignore',
    selectAll: 'Select all',
    columns: {
      document: 'Document',
      filed: 'Filed as',
      detected: 'Looks like',
      reason: 'Reason',
      since: 'Since',
      attempt: 'Attempt',
      class: 'Group',
      acknowledgedBy: 'Ignored by',
      acknowledgedAt: 'Ignored on',
    },
    // Guarded writes name their dialog as `{ title, body? }` — the title is the question, the
    // body is what the write will act on and what it costs. `lib/ask.tsx` renders both.
    tryAgainConfirm: (count: number, failureClass?: string) => ({
      title: `Try again on ${count} document${count === 1 ? '' : 's'}?`,
      body:
        failureClass === 'misfiled'
          ? 'MBGE reads the same file from the same folder. Unless you replaced the file, it stays in Wrong folder.'
          : 'MBGE processes the documents again from the start, with the file that is in the inbox now. When a document completes, it leaves this list.',
    }),
    acknowledgeConfirm: (count: number) => ({
      title: `Ignore ${count} document${count === 1 ? '' : 's'}?`,
      body: 'They leave this list and stop counting toward alerts.',
    }),
    unignoreConfirm: (docId: string) => ({
      title: `Un-ignore ${docId}?`,
      body: 'The document returns to the list of documents that MBGE did not process.',
    }),
    unknownError: 'unknown error',
    isFloorNote: 'More may exist past this page; the count and age shown are a lower bound.',
  },
  extractionRules: {
    loading: 'Loading…',
    loadFailed: "Couldn't reach the console's own backend. Try reloading.",
    modeAuto: 'Auto: follows new minor and patch versions. A new major version waits for approval.',
    modePinned: (version: string) => `pinned @ ${version}`,
    modePinnedNoVersion: 'pinned',
    inUse: 'In use',
    none: 'none',
    summaryChanged: 'Changed',
    summaryBy: 'by',
    versionListHeading: 'Version list',
    activeMark: 'In use',
    keptNote: 'shown on every page',
    versionColumns: { version: 'Version', published: 'Published', status: 'Status' },
    waitingHeading: 'Waiting for approval',
    pin: 'Pin this version',
    pinVersion: (version: string) => `Pin version ${version}`,
    acceptVersion: (version: string) => `Accept version ${version}`,
    unpin: 'Unpin this type',
    decline: 'Decline (pin to current)',
    pinHelp: 'Pinning keeps this type on the selected version until you unpin it.',
    reasonLabel: 'Reason',
    pinConfirm: (type: string, from: string, to: string) => ({ title: `Pin ${type} from ${from} to ${to}?` }),
    unpinConfirm: (type: string) => ({ title: `Resume auto tracking for ${type}?` }),
    acceptConfirm: (type: string, from: string, to: string) => ({
      title: `Move ${type} from ${from} to ${to}?`,
      body: `${type} moves to ${to} and keeps tracking new versions automatically.`,
    }),
    declineConfirm: (type: string, current: string, waiting: string) => ({
      title: `Keep ${type} on ${current}?`,
      body: `This pins ${type} to ${current} and declines ${waiting}. Auto tracking stops until an operator unpins ${type}.`,
      confirmLabel: `Decline ${waiting}`,
    }),
    waitingVersion: (version: string, since: string) => `Version ${version}, waiting since ${since}.`,
    backfillWarning: (inFlight: number, left: number) =>
      `A backfill run is active: ${inFlight} document(s) already in flight on the current version, ${left} left in the run.`,
  },
  why: {
    heading: 'Why documents escalated',
    loading: 'Loading…',
    loadFailed: "Couldn't reach the console's own backend. Try reloading.",
    reasonClassLabels: {
      'reconciliation-failure': 'Totals don’t add up',
      'cross-document-mismatch': 'Documents disagree',
      'required-field-below-threshold': 'Hard to read',
      'schema-violation': 'Missing a required field',
      'catalog-or-format': 'Value not recognized',
      'confidence-below-threshold': 'Low confidence in a value',
      'engine-error': 'The check could not run',
    } as Record<string, string>,
    unrecorded: 'Reason not recorded',
    columns: { document: 'Document', layout: 'Layout', reason: 'Reason' },
    breakdownColumns: { reason: 'Reason', count: 'Count' },
  },
  documentType: {
    pedimento: 'Pedimento',
    cove: 'COVE',
    factura: 'Factura',
    guia: 'Guía',
    'lista-empaques': 'Lista de Empaques',
  } as Record<string, string>,
  sendToReview: {
    heading: (source: string) => (source === 'spot-check' ? 'Send to review — random checks' : 'Send to review'),
    loading: 'Loading…',
    loadFailed: "Couldn't reach the console's own backend. Try reloading.",
    empty: 'Nothing is waiting.',
    close: 'Close',
    selectAll: 'Select all',
    isFloorNote: 'More may exist past this page; the count shown is a lower bound.',
    columns: { document: 'Document', type: 'Type' },
    sendButton: (count: number) => `Send to review · ${count}`,
    // QA-13: only the selected documents go, so a partial selection needs no warning.
    confirm: (count: number) => ({
      title: `Send ${count} document${count === 1 ? '' : 's'} to review?`,
      body: 'This creates a Data Studio project and commits reviewer time.',
    }),
    // QA-149: why a document was not sent. `short` is the text on the row, `tip` is the InfoTip.
    // An unknown reason shows its code and no tip.
    skipReasons: {
      'no-source-pdf': {
        short: 'Original file not found.',
        tip: 'MBGE cannot find the PDF of this document, so it cannot go to Data Studio. Tell your administrator.',
      },
      'no-routing-claim': {
        short: 'Original file not found.',
        tip: 'MBGE cannot find the PDF of this document, so it cannot go to Data Studio. Tell your administrator.',
      },
      'partial-page-range': {
        short: 'Part of a larger file.',
        tip: 'MBGE does not send part of a file to review.',
      },
      'no-skill-version': {
        short: 'No extraction rules version.',
        tip: 'Tell your administrator.',
      },
      'other-skill-version': {
        short: 'Goes with the next send.',
        tip: 'One send holds one extraction rules version. Send again after this batch.',
      },
      'open-batch': {
        short: 'Waits for the open batch.',
        tip: 'A batch of this type is still being sent. Send again when it ends.',
      },
    } as Record<string, { short: string; tip: string }>,
    skipShort: (reason: string) => labels.sendToReview.skipReasons[reason]?.short ?? reason,
    skipTip: (reason: string, detail?: string) => {
      const tip = labels.sendToReview.skipReasons[reason]?.tip;
      return [tip, detail].filter(Boolean).join(' ');
    },
    // On a waiting document, from the newest send of its type that skipped it.
    notSentNote: (at: string, reason: string) => `Not sent on ${formatDay(at)}: ${labels.sendToReview.skipShort(reason)}`,
    notSentLabel: (docId: string) => `why ${docId} was not sent`,
    // In the send report, for each skipped document.
    notSent: (stem: string, reason: string) => `${stem}: ${labels.sendToReview.skipShort(reason)}`,
    reasonLabel: 'Why are you sending?',
    reported: 'Sent. Here is what happened for each type.',
    // ESC-20: escalation answers once the batch is staged; the project is made after that.
    groupReport: (type: string, count: number, batchId: string | null, error?: string, status?: string, skipped = false) => {
      const name = documentTypeLabel(type);
      if (error) return `${name}: failed. ${error}`;
      if (!batchId) return skipped ? `${name}: none sent.` : `${name}: nothing to send.`;
      const state = status ? ` (${batchStatusLabel(status)})` : '';
      return `${name}: ${count} document${count === 1 ? '' : 's'} sent, batch ${batchId}${state}.`;
    },
    // QA-114 (client finding 10): say where the review happens, as visible text, not a tooltip.
    // "Created" only once the batch manifest has the project id.
    projectReport: (projectId: string | undefined) =>
      projectId
        ? `A project has been created in Data Studio for human review (project ${projectId}).`
        : 'The Data Studio project for human review is being created.',
    batchStatus: {
      claimed: 'claimed',
      uploading: 'uploading the documents',
      launching: 'creating the project',
      launched: 'waiting for the project',
      delivered: 'moving to review',
      'in-review': 'in review',
      'results-fetched': 'reviewed',
      failed: 'failed',
      abandoned: 'released',
      deleted: 'project deleted',
    } as Record<string, string>,
    stuckHeading: 'Stuck batches',
    stuckNote: (minutes: number) =>
      `A batch is stuck when it failed, when it has not reached moving to review ${minutes} minutes after it was sent, or when its documents stay in its batch folder for more than ${minutes} minutes.`,
    stuckColumns: { type: 'Type', batch: 'Batch', status: 'Status', age: 'Age' },
    stuckReasons: {
      failed: 'failed',
      'not-delivered': 'not delivered',
      'stems-pending': 'documents left in the batch folder',
    } as Record<string, string>,
    stuckStatus: (status: string, reason?: string) =>
      `${batchStatusLabel(status)}${reason ? `, ${labels.sendToReview.stuckReasons[reason] ?? reason}` : ''}`,
    age: formatDuration,
    // QA-120: when the results poller last finished a run.
    pollerLine: (poller: { state: string; startedAt?: string; finishedAt?: string }): string | null => {
      switch (poller.state) {
        case 'ok':
          return `Answers last checked at ${formatStatusDate(poller.finishedAt ?? '')}.`;
        case 'stale':
          return `Answers last checked at ${formatStatusDate(poller.finishedAt ?? '')}. The check is late. Tell Datasaur.`;
        case 'unfinished':
          return `A check of the answers started at ${formatStatusDate(poller.startedAt ?? '')} and did not end. Tell Datasaur.`;
        case 'starting':
          return 'The first check of the answers is running.';
        case 'off':
          return 'Human review is off: Data Studio is not set up.';
        case 'never':
          return 'Answers are not checked yet.';
        default:
          return null;
      }
    },
  },
  pauseEverything: {
    heading: 'Pause everything',
    // Finding 9: the Pipeline status card of Figma `2158:13995` replaces the full-width red bar.
    // Its button is "Pause pipeline" with no count, and its confirm dialog states no count either.
    cardHeading: 'Pipeline status',
    cardBody: 'Pause stops new documents. Documents in progress finish.',
    cardTipLabel: 'what a pause does',
    cardTip:
      'New files wait in the inbox, and Try again waits until you resume. Documents already in progress finish, and their paid jobs continue. Human review, random checks, and alert emails keep running.',
    // QA-128: the paused card's counts, from the First pass strip. A floor reads "at least".
    counts: (finishing: number, finishingIsFloor: boolean, waiting: number, waitingIsFloor: boolean) =>
      `${finishingIsFloor ? 'At least ' : ''}${finishing.toLocaleString('en-US')} finishing, ${waitingIsFloor ? 'at least ' : ''}${waiting.toLocaleString('en-US')} waiting.`,
    seeMovement: 'See First pass movement',
    statusRunning: 'Status: Running.',
    statusPaused: (when: string | null, by: string | null) =>
      `Status: Paused${when ? `, last updated on ${when}` : ''}${by ? ` by ${by}` : ''}.`,
    // The paused row's second text (`condition-copy.ts`): the pause flag could not be read.
    statusUnreadable: "Status: Paused. The pause flag couldn't be read, so processing is held as a precaution.",
    pauseButton: 'Pause pipeline',
    resumeButton: 'Resume pipeline',
    resumeConfirm: {
      title: 'Resume the whole pipeline?',
      body: 'MBGE starts to process the documents that wait in your inbox, and the new ones as they arrive.',
    },
    pauseConfirm: {
      title: 'Pause pipeline?',
      body: 'New documents wait in the inbox until you resume. Documents in progress will finish.',
    },
    pauseConfirmButton: 'Pause',
    reasonLabel: 'Why are you pausing?',
  },
  settings: {
    loading: 'Loading…',
    loadFailed: "Couldn't reach the console's own backend. Try reloading.",
    waitHeading: 'How long may things wait',
    humanReview: 'Documents waiting for a person to review them',
    deadLetter: 'Documents that MBGE did not process',
    spotCheck: 'Documents waiting for a random check',
    // QA-132: both review wait limits need Data Studio to ever clear.
    waitLimitTip: "Leave empty if you don't use Data Studio. Otherwise this alert never clears.",
    stalled: 'Parked documents and queued model jobs that do not move',
    burstCount: 'Documents that fail in a short time, more than',
    burstWindow: 'The short time for failed documents',
    unitDocuments: 'documents',
    waitHelp: 'A line appears on Processing when something passes its limit. Most also send an email.',
    whoHeading: 'Who gets told',
    recipients: 'Email goes to',
    editRecipients: 'Edit',
    pauseGrace: 'Wait this long before reporting a pause',
    notSet: 'not set',
    unitDays: 'days',
    unitHours: 'hours',
    unitMinutes: 'minutes',
    saveButton: (count: number) => `Save · ${count} change${count === 1 ? '' : 's'}`,
    noChanges: 'Nothing has changed yet.',
    saveConfirm: (count: number) => ({
      title: `Save ${count} change${count === 1 ? '' : 's'}?`,
      body: 'The new limits and email addresses replace the current ones. MBGE records the change in the settings history.',
    }),
    saved: 'Saved.',
    invalidNumber: 'Enter a number above 0, or leave the field empty.',
    // The limit is 365 days; `amount` is that limit in the field's own `unit`.
    tooLarge: (amount: number, unit: string) =>
      `Enter at most ${amount.toLocaleString('en-US')} ${unit}${unit === 'days' ? '' : ' (365 days)'}, or leave the field empty.`,
    recipientsPrompt: 'Email addresses, separated by semicolons:',
  },
  apiClients: {
    heading: 'API clients',
    // The token URL is machine data, so the panel sets it in mono between these two parts. The URL
    // is Keycloak's, so the panel shows the line only after the backend has sent it.
    help: {
      before: 'A caller trades its client ID and secret at ',
      after: ' (grant type client_credentials) for a token.',
    },
    // The token lives on after a new secret; a disable, or a removed api-client mapper (SSO-20
    // proof: 30.1 seconds), ends it within the 30 seconds that the console keeps an answer of
    // Keycloak (ADR 0043, ADR 0044).
    effects:
      'A new secret does not end the token that a caller already has. A disable ends it within 30 seconds. A super admin ends it as fast by removing the api-client mapper of the client in the user admin console.',
    superAdmin: 'A super admin can also do these in the user admin console, except create.',
    loading: 'Loading…',
    loadFailed: "Couldn't load the API clients. Try reloading.",
    empty: 'No API clients yet.',
    columns: {
      name: 'Name',
      id: 'Client ID',
      createdAt: 'Created',
      createdBy: 'Created by',
      tokenMinutes: 'Token lasts',
      status: 'Status',
    },
    tokenMinutes: (minutes: number) => `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`,
    tokenMinutesUnknown: 'Realm default',
    noValue: '—',
    active: 'Active',
    disabled: 'Disabled',
    noApiAccess: 'No API access',
    createButton: 'Create client',
    createConfirm: { title: 'Create an API client?', body: 'The secret shows once, right after you create the client.' },
    nameLabel: 'Client name',
    revealed: (name: string) => `Client "${name}" created.`,
    revealedSecret: (name: string) => `New secret for client "${name}".`,
    copySecretNow: 'Copy the secret now. You cannot see it again.',
    clientIdLabel: 'Client ID',
    secretLabel: 'Secret',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Copy did not work. Select the text and copy it by hand.',
    unreachable: 'The console did not answer. Check the connection and try again.',
    dismiss: 'Done',
    newSecretButton: 'New secret',
    newSecretConfirm: (name: string, id: string) => ({
      title: `Make a new secret for ${name}?`,
      body: `Client ID ${id}. The old secret stops working for new tokens. A token that a caller already has stays valid until it ends. The new secret shows once.`,
    }),
    minutesButton: 'Change minutes',
    minutesLabel: 'Token minutes (1 to 1440)',
    minutesConfirm: (name: string, id: string) => ({
      title: `Change the token minutes of ${name}?`,
      body: `Client ID ${id}. New tokens last this many minutes. A token that a caller already has keeps its own end time.`,
    }),
    minutesInvalid: 'Enter a whole number from 1 to 1440.',
    disableButton: 'Disable',
    disableConfirm: (name: string, id: string) => ({
      title: `Disable ${name}?`,
      body: `Client ID ${id}. The client gets no new token, and a token that a caller already has stops working within 30 seconds. You can enable the client again.`,
    }),
    enableButton: 'Enable',
    enableConfirm: (name: string, id: string) => ({
      title: `Enable ${name}?`,
      body: `Client ID ${id}. The system that uses this client can get a new token again.`,
    }),
    revokeButton: 'Revoke',
    revokeConfirm: (name: string, id: string) => ({
      title: `Revoke ${name}?`,
      body: `Client ID ${id}. Callers that use this client stop working at once. You cannot undo this.`,
    }),
  },
  randomChecks: {
    heading: 'Random checks',
    sub: 'A weekly quality check of automatically accepted documents.',
    headingTip:
      "Each week MBGE picks a random share of the documents it accepted with no human review. The share comes from your target and last week's volume, so a week can pick more or fewer. Send them to review from Processing; two reviewers check each one in Data Studio.",
    columnTips: {
      perWeek: 'How many documents to check each week.',
      checkedLastWeek: 'Checks the reviewers finished in the last 7 days.',
      ofArrivals: "Share of last week's documents that were checked.",
      accuracy:
        "Share of the last 7 days' checked documents with at least 95% of required fields right and every ID field exact.",
    },
    // QA-132: shown above the table when a random-check send cannot work.
    needsDataStudio: 'Random checks need Data Studio. Ask your administrator to set it up.',
    loading: 'Loading…',
    loadFailed: "Couldn't reach the console's own backend. Try reloading.",
    columns: {
      type: 'Type',
      perWeek: 'Per week',
      checkedLastWeek: 'Checked last week',
      ofArrivals: 'Of arrivals',
      accuracy: 'Accuracy',
    },
    accuracyCell: (percent: number, sample: number) => `${percent}% of ${sample}`,
    notSet: 'not set',
    changeButton: 'Change what gets checked',
    changeLabel: (type: string) => `New weekly target for ${type}`,
    // One dialog, not three: the current value is the body, the new value is a field, and the
    // Change button is the confirmation the old `window.confirm` step used to be.
    changeConfirm: (type: string, from: number | null) => ({
      title: `Change what gets checked — ${type}?`,
      body: `Currently ${from ?? 'not set'} per week.`,
    }),
  },
  /** QA-89: the two buttons that Test extraction and the document page share. */
  resultJson: {
    download: 'Download JSON',
    copy: 'Copy JSON',
    copied: 'Copied.',
    copyFailed: "Couldn't copy. Use Download JSON instead.",
  },
  testExtraction: {
    heading: 'Test extraction',
    sub: 'Upload one PDF, or a PDF with its XML, and follow it to its result.',
    // The user decided that a test run is a normal document (QA-34): no sandbox mode.
    normalDocument: 'Test runs create production results and may incur OCR charges.',
    normalDocumentTip:
      'A test run goes through the same steps as a file in the inbox. It counts on Processing, shows in search and the Results API, and can go to review, random checks, and alerts. Only a scanned file starts a paid OCR job.',
    typeLabel: 'Document type',
    typePlaceholder: 'Choose a type',
    filesLabel: 'Files (.pdf and optional .xml)',
    chooseFiles: 'Choose files',
    noFileChosen: 'No file chosen',
    upload: 'Upload',
    uploading: 'Uploading…',
    errors: {
      noType: 'Choose a document type.',
      noFile: 'Choose a PDF.',
      xmlOnly: 'An XML file needs its PDF. Add the PDF too.',
      tooMany: 'Choose at most two files: one PDF and one XML.',
      wrongKind: 'Only .pdf and .xml files are accepted.',
      twoOfAKind: 'Choose one PDF and at most one XML.',
    },
    confirm: {
      title: 'Upload this file for a test run?',
      body: 'A scanned file starts a paid OCR job and can take 15 to 30 minutes or more.',
    },
    uploaded: (docId: string) => `Uploaded as ${docId}.`,
    runsHeading: 'Test runs',
    runsEmpty: 'No test runs yet.',
    loading: 'Loading…',
    loadFailed: "Couldn't reach the console's own backend. Try reloading.",
    uploadedBy: (by: string) => `Uploaded by ${by}`,
    states: {
      waiting: 'Waiting for the inbox watcher',
      'in-progress': 'In progress',
      completed: 'Done',
      'not-processed': 'Not processed',
      ignored: 'Ignored',
    } as Record<string, string>,
    waitingPaused: 'The inbox is paused, so the watcher will not take it until someone resumes.',
    inProgressNote: 'A scanned file can take 15 to 30 minutes or more.',
    ignoredNote: 'Someone set it aside on Not processed.',
    accepted: 'The gate accepted it.',
    escalated: (reason: string | undefined) => (reason ? `The gate sent it on: ${reason}.` : 'The gate sent it on.'),
    detectedAs: (type: string) => `Looks like a ${type}. Run it again as that type, then remove this row.`,
    showResult: 'Show the result',
    hideResult: 'Hide the result',
    resultMissing: 'The result is not readable yet.',
    runAgain: 'Run again',
    runAgainTypeLabel: 'Type of the new run',
    runAgainConfirm: (docId: string) => ({
      title: `Run ${docId} again?`,
      body: 'It uploads the same stored file again as a new test run, with the type you choose. A scanned file starts a paid OCR job and can take 15 to 30 minutes or more. The old row stays.',
    }),
    removeRun: 'Remove from list',
    removeTooltip: 'Remove this row. Its Not processed entry moves to Ignored.',
    removeConfirm: (docId: string) => ({
      title: `Remove ${docId} from the list?`,
      body: 'The row leaves this list. Its Not processed entry moves to Ignored, by you. Its files stay stored.',
    }),
  },
  stub: (route: string) => `${route} ships in a later row (see the register).`,
} as const;

export const documentTypeOrder = ['pedimento', 'cove', 'factura', 'guia', 'lista-empaques'] as const;

export function sortByDocumentType<T extends { type: string }>(items: readonly T[]): T[] {
  const order = new Map<string, number>(documentTypeOrder.map((type, index) => [type, index]));
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const orderDifference =
        (order.get(left.item.type) ?? documentTypeOrder.length) - (order.get(right.item.type) ?? documentTypeOrder.length);
      return orderDifference || left.index - right.index;
    })
    .map(({ item }) => item);
}

export function batchStatusLabel(status: string): string {
  return labels.sendToReview.batchStatus[status] ?? status;
}

export function documentTypeLabel(type: string): string {
  return labels.documentType[type] ?? type;
}

export function reasonClassLabel(reasonClass: string): string {
  return labels.why.reasonClassLabels[reasonClass] ?? reasonClass;
}

export function failureClassHeading(failureClass: string): string {
  return labels.notProcessed.classHeadings[failureClass] ?? failureClass;
}

export type BrowseOutcomeKey = keyof typeof labels.documents.browse.outcomes;

/** The outcome of a document from its latest index line, as a key: the Documents status filter,
 *  sort, and pill tone use it, and `browseOutcome` names it. */
export function browseOutcomeKey(row: { tier?: string; gateVerdict?: string }): BrowseOutcomeKey {
  if (row.tier === 'reviewed') return 'reviewed';
  return row.gateVerdict === 'escalate' ? 'review' : 'accepted';
}

/** The outcome of a document from its latest index line. */
export function browseOutcome(row: { tier?: string; gateVerdict?: string }): string {
  return labels.documents.browse.outcomes[browseOutcomeKey(row)];
}

/** One link voice: a link to another page ends in an arrow, whichever way the backend wrote it. */
export function linkLabel(label: string): string {
  return label.endsWith('→') ? label : `${label} →`;
}

/** A repeating group's name is a schema key such as `lista_empaques`; the page head reads `Lista empaques`. */
export function groupHeading(name: string): string {
  const words = name.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
